import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit"
import {
  deployActivePriorityListsToSite,
  getPlaylistById,
  getPlaylists,
} from "./api"
import { allowUpdate } from "../../app/store"

/**
 *  State management for playlists
 */
const initialState = {
  deploying: {}, // space for new deploying statuses
  byId: {}, // A map of playlists by id
  matchesByCtx: {
    // root context powering the playlists list view
    root: {
      _loadStatus: "idle", // Information about the loading status of a call
      _errorMessage: undefined,
      ids: [], // playlists by id
      page: {}, // Pagination information
    },
  },
}

/**
 * Thunk to perform async logic to deploy the active priorities to an Astrosite
 * @type {AsyncThunk<*, void, {}>}
 */
export const deployActivePriorityLists = createAsyncThunk(
  "playlists/generate",
  async (
    {
      astrosite,
      inject_calibration_task,
      deploy_to_astrosite,
      priority_list_ids,
    },
    { rejectWithValue }
  ) => {
    try {
      return await deployActivePriorityListsToSite({
        astrosite,
        inject_calibration_task,
        deploy_to_astrosite,
        priority_list_ids,
      })
    } catch (error) {
      throw rejectWithValue(error.message)
    }
  }
)

/**
 * Thunk to perform async logic to fetch the playlists matching the provided filters
 *
 * @type {AsyncThunk<*, {readonly filter?: *, readonly context?: *, readonly page?: *}, {}>}
 */
export const fetchPlaylists = createAsyncThunk(
  "playlists/fetch",
  /**
   *
   * @param filter the filters to apply to the search
   * @param page Pagination information
   * @param context The context from where the query is being made.
   * @returns {Promise<*>}
   */
  async ({ filter, page, context }) => {
    const response = await getPlaylists({
      filter,
      page,
    })
    if (context) {
      response.context = context
    }
    return response
  }
)

/**
 * Thunk to perform async logic to fetch a playlist by its ID
 * @type {AsyncThunk<*, void, {}>}
 */
export const fetchPlaylistById = createAsyncThunk(
  "playlist/fetch",
  async (noradId) => {
    const response = await getPlaylistById(noradId)
    return response
  }
)

/**
 * Playlists reducer
 */
export const playlistSlice = createSlice({
  name: "playlists",
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(deployActivePriorityLists.pending, (state) => {
        state.deploying = { _saveStatus: "updating" }
      })
      .addCase(deployActivePriorityLists.fulfilled, (state, action) => {
        state.deploying._saveStatus = "idle"
        state.deploying = { ...state.deploying, ...action.payload }
      })
      .addCase(deployActivePriorityLists.rejected, (state, action) => {
        state.deploying._saveStatus = "rejected"
        state.deploying = { ...state.deploying, ...action.payload }
      })
      .addCase(fetchPlaylists.pending, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (!state.matchesByCtx[context]) {
          state.matchesByCtx[context] = {
            ids: [],
            page: {},
          }
        }
        state.matchesByCtx[context]._loadStatus = "loading"
      })
      .addCase(fetchPlaylists.fulfilled, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (state.matchesByCtx[context]) {
          state.matchesByCtx[context]._loadStatus = "idle"

          if (allowUpdate(state.matchesByCtx[context].at, action.payload.at)) {
            // Don't reset if we are paging
            if (action?.payload?.page?.start_index === 0) {
              state.matchesByCtx[context].ids = []
            }
            action?.payload?.data?.map((act) => {
              // Add the playlist to state by id (this can include ids not retrieved in this call)
              state.byId[act.id] = {
                ...act,
                _at: action.payload.at,
              }
              // add the ids that match from this query
              if (state.matchesByCtx[context].ids.indexOf(act.id) === -1) {
                state.matchesByCtx[context].ids.push(act.id)
              }
            })
            state.matchesByCtx[context].page = action.payload.page
            state.matchesByCtx[context].at = action.payload.at
          }
        }
      })
      .addCase(fetchPlaylists.rejected, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        state.matchesByCtx[context]._loadStatus = "rejected"
        state.matchesByCtx[context]._errorMessage = action?.error?.message
      })
      .addCase(fetchPlaylistById.pending, (state, action) => {
        if (!state.byId[action.meta.arg]) {
          state.byId[action.meta.arg] = {}
        }
        state.byId[action.meta.arg]._loadStatus = "loading"
      })
      .addCase(fetchPlaylistById.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          if (
            allowUpdate(
              state.byId[action.payload.id]
                ? state.byId[action.payload.id]._at
                : undefined,
              action.payload._at
            )
          ) {
            state.byId[action.payload.id] = {
              ...action.payload,
              _at: action.payload._at,
            }
          }

          state.byId[action.payload.id]._loadStatus = "idle"
        }
      })
      .addCase(fetchPlaylistById.rejected, (state, action) => {
        state.byId[action.meta.arg]._loadStatus = "rejected"
        state.byId[action.meta.arg]._errorMessage += action.payload
      })
  },
})
export const selectDeploying = (state) => state.playlists.deploying

// Selector for all playlists
export const selectPlaylistsById = (state) => state.playlists.byId
// Selector for the known types of actions

/**
 * Takes in a context parameter and returns matches from matchesByContext[context]
 * Usage in components: const items = useSelector((state) => selectPlaylistsByContext(state, `rso/${id}`))
 */
export const selectPlaylistsByContext = createSelector(
  [
    // first input - extract value from `state`
    (state) => state.playlists.matchesByCtx,
    // Take the second arg, `context`, and forward to the output selector
    (state, context) => context,
  ],
  // Output selector gets (`playlists, context)` as args
  (matchesByCtx, context) => {
    return matchesByCtx[context]
  }
)

/**
 * Takes in a list of ids as a parameter and returns matches from byIds
 * Usage in components: const items = useSelector((state) => selectPlaylistByIds(state, ["1", "2"]))
 */
export const selectPlaylistByIds = createSelector(
  [
    // first input - extract value from `state`
    selectPlaylistsById,
    // Take the second arg, `ids`, and forward to the output selector
    (state, ids) => ids,
  ],
  // Output selector gets (`byId, ids)` as args
  (byId, ids) => {
    return ids.map((id) => byId[id] || { id })
  }
)
export default playlistSlice.reducer
